/* Beta product photography, turned into clean front elevations. Each photo is calibrated with the four corners of the
   cabinet's front face; the face is warped flat (true perspective, not just a crop), and the white studio background
   left at the corners is filled with a dark shadow tone so cabinets sit flush. Shared by the 2D scene (as data URLs)
   and the 3D view (as canvases). Falls back silently if an image cannot be read. */
(function () {
  'use strict';
  if (window.SBPhotos) return;
  const cache = new Map(), listeners = [], imgs = new Map();
  const load = url => {
    if (!imgs.has(url)) imgs.set(url, new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = url; }));
    return imgs.get(url);
  };
  const keyOf = ph => ph.url + '|' + ph.cfg.quad.join(',') + '|' + ph.cfg.ow + 'x' + ph.cfg.oh;
  // Map the triangle (s0,s1,s2) of the source image onto (d0,d1,d2) of the canvas.
  function tri(x, img, s, d) {
    const [s0, s1, s2] = s, [d0, d1, d2] = d;
    const det = (s1[0] - s0[0]) * (s2[1] - s0[1]) - (s2[0] - s0[0]) * (s1[1] - s0[1]);
    if (!det) return;
    const a = ((d1[0] - d0[0]) * (s2[1] - s0[1]) - (d2[0] - d0[0]) * (s1[1] - s0[1])) / det,
      b = ((d1[1] - d0[1]) * (s2[1] - s0[1]) - (d2[1] - d0[1]) * (s1[1] - s0[1])) / det,
      c = ((d2[0] - d0[0]) * (s1[0] - s0[0]) - (d1[0] - d0[0]) * (s2[0] - s0[0])) / det,
      e = ((d2[1] - d0[1]) * (s1[0] - s0[0]) - (d1[1] - d0[1]) * (s2[0] - s0[0])) / det;
    const cx = (d0[0] + d1[0] + d2[0]) / 3, cy = (d0[1] + d1[1] + d2[1]) / 3, grow = p => [p[0] + Math.sign(p[0] - cx) * 1.1, p[1] + Math.sign(p[1] - cy) * 1.1];
    x.save(); x.beginPath();
    const g0 = grow(d0), g1 = grow(d1), g2 = grow(d2);
    x.moveTo(g0[0], g0[1]); x.lineTo(g1[0], g1[1]); x.lineTo(g2[0], g2[1]); x.closePath(); x.clip();
    x.setTransform(a, b, c, e, d0[0] - a * s0[0] - c * s0[1], d0[1] - b * s0[0] - e * s0[1]);
    x.drawImage(img, 0, 0);
    x.restore();
  }
  function clean(cv) {
    const w = cv.width, h = cv.height, x = cv.getContext('2d'), d = x.getImageData(0, 0, w, h), px = d.data, seen = new Uint8Array(w * h), stack = [];
    const white = i => { const r = px[i], g = px[i + 1], b = px[i + 2]; return r > 226 && g > 226 && b > 226 && Math.max(r, g, b) - Math.min(r, g, b) < 24; };
    const push = (cx, cy) => { const k = cy * w + cx; if (!seen[k] && white(k * 4)) { seen[k] = 1; stack.push(k); } };
    for (let i = 0; i < w; i++) { push(i, 0); push(i, h - 1); }
    for (let j = 0; j < h; j++) { push(0, j); push(w - 1, j); }
    while (stack.length) {
      const k = stack.pop(), cx = k % w, cy = (k / w) | 0, i = k * 4;
      px[i] = 24; px[i + 1] = 27; px[i + 2] = 30; px[i + 3] = 255;
      if (cx > 0) push(cx - 1, cy); if (cx < w - 1) push(cx + 1, cy); if (cy > 0) push(cx, cy - 1); if (cy < h - 1) push(cx, cy + 1);
    }
    x.putImageData(d, 0, 0);
    return cv;
  }
  async function build(ph) {
    const c = ph.cfg, img = await load(ph.url), sx = img.naturalWidth / c.nw, sy = img.naturalHeight / c.nh;
    const q = c.quad.map(p => [p[0] * sx, p[1] * sy]), W = c.ow, H = c.oh, N = 14;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const x = cv.getContext('2d'); x.fillStyle = '#1d2023'; x.fillRect(0, 0, W, H);
    const P = (u, v) => [(1 - u) * (1 - v) * q[0][0] + u * (1 - v) * q[1][0] + u * v * q[2][0] + (1 - u) * v * q[3][0], (1 - u) * (1 - v) * q[0][1] + u * (1 - v) * q[1][1] + u * v * q[2][1] + (1 - u) * v * q[3][1]];
    // Coarse pass first, so any anti-aliased seam in the fine mesh blends with a similar colour instead of the backdrop.
    tri(x, img, [P(0, 0), P(1, 0), P(1, 1)], [[0, 0], [W, 0], [W, H]]); tri(x, img, [P(0, 0), P(1, 1), P(0, 1)], [[0, 0], [W, H], [0, H]]);
    for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) {
      const u0 = i / N, u1 = (i + 1) / N, v0 = j / N, v1 = (j + 1) / N;
      const s00 = P(u0, v0), s10 = P(u1, v0), s11 = P(u1, v1), s01 = P(u0, v1), d00 = [u0 * W, v0 * H], d10 = [u1 * W, v0 * H], d11 = [u1 * W, v1 * H], d01 = [u0 * W, v1 * H];
      tri(x, img, [s00, s10, s11], [d00, d10, d11]); tri(x, img, [s00, s11, s01], [d00, d11, d01]);
    }
    return clean(cv);
  }
  function entry(ph) {
    const k = keyOf(ph);
    let e = cache.get(k);
    if (!e) {
      e = {};
      e.promise = build(ph).then(cv => { e.canvas = cv; try { e.url = cv.toDataURL('image/jpeg', .92); } catch (err) { e.failed = true; } listeners.forEach(f => f()); return cv; }).catch(() => { e.failed = true; throw new Error('photo'); });
      e.promise.catch(() => {});
      cache.set(k, e);
    }
    return e;
  }
  window.SBPhotos = {
    get(ph) { const e = entry(ph); return e.failed ? null : e.url || null; },   // sync data URL, or null while loading
    canvas(ph) { return entry(ph).promise; },                                    // async canvas for the 3D view
    onReady(fn) { listeners.push(fn); }
  };
})();
